import { useEffect, useState } from "react";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import { DepositHistoryListResponse } from "../../models/response/depositHistoryListResponse";
import {
  getDepositHistory,
  getUserLastDeposit,
  revertUserDepositById,
  validateUsersDepositTransactionUpdateById,
  addUsersDepositTransactionUpdateRequest,
  uploadTransactionUpdateDocument,
} from "../../services/transactions.service";
import { ApiDateFormatRequest } from "../../../../models/Request/apiDateFormatRequest";
import { BlobContainerConsts } from "../../../../models/constants/blobContainerConsts";
import { BLOB_IMAGE_URL } from "../../../../config/appConfig";
import { TransactionStatusTypes } from "../../../../models/constants/transactionStatusTypes";
import { Button } from "../../../../components/ui";
import { ConfirmationModal } from "../../../../components/ui/modals/ConfirmationModal";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import { ValidateTransactionUpdateResponse } from "../../models/response/validateTransactionUpdateResponse";
import { UploadDocumentModal } from "../../../../components/ui/modals/UploadDocumentModal";
import { UploadDocumentRequest } from "../../models/request/uploadDocumentRequest";
import { DepositTransactionUpdateRequest } from "../../models/request/depositTransactionUpdateRequest";
import { DEPOSIT_STATUS_STYLE } from "../../models/constants/statusStyles";
import localization from "../../../../config/localization";
import InfiniteScroll from "../../../../components/ui/InfiniteScroll";
import TransactionCard from "../../../../components/ui/TransactionCard";
import TransactionFilter from "./TransactionFilters";
import common from "../../../../utils/common";
import { userPendingStore } from "../../../users/store/userPendingData.store";
import { useRealTimeStore } from "../../../../store/signalrRealTime.store";
import { useAppDispatch } from "../../../../store/hooks";
import { changeLoaderState } from "../../../../reducers/appStateReducer";

export default function Deposit() {
  const dispatch = useAppDispatch();
  type Filters = {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  };

  const Default_Filters: Filters = {
    searchText: null,
    fromDate: null,
    toDate: null,
    statuses: [] as string[],
  };

  const [loading, setLoading] = useState(true);
  const [openRowIds, setOpenRowIds] = useState<string[]>([]);
  const [data, setItems] = useState<DepositHistoryListResponse | null>(null);
  const [pageNo, setPageNo] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filters, setFilters] = useState<Filters>(Default_Filters);

  const [ValidateTransactionUpdateData, setValidateTransactionUpdateResponse] =
    useState<Record<string, ValidateTransactionUpdateResponse | null>>({});
  const [selectedDetailsItemId, setSelectedDetailsItemId] = useState<
    string | null
  >(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);
  const [isRevertAllowed, setIsRevertAllowed] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isRequestUpdateModalOpen, setIsRequestUpdateModalOpen] =
    useState(false);
  const latestDepositUpdate = useRealTimeStore((state) => state.latestDepositUpdate);
  const depositRevert = useRealTimeStore((state) => state.depositRevertAllowed);

  const handleOpenRowIds = (itemId: string, isOpen: boolean) => {
    setOpenRowIds((prev) => {
      if (isOpen) {
        if (!prev.includes(itemId)) {
          return [...prev, itemId];
        }
        return prev;
      } else {
        return prev.filter((id) => id !== itemId);
      }
    });
  };

  function createDepositHistoryRequest(
    pageNo: number,
    filters: {
      searchText: string | null;
      fromDate: string | null;
      toDate: string | null;
      statuses: string[];
    },
  ): ApiDateFormatRequest {
    return {
      pageNo: pageNo,
      pageSize: 20,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      transactionId: filters.searchText?.trim() || null,
      status: filters.statuses.length ? filters.statuses.join(",") : null,
      isCallFromMobileDevice: false,
    };
  }

  const handleFiltersChange = (updated: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updated }));
  };

  const fetchData = async (isReset = false, appliedFilters = filters) => {
    if (isLoadingMore && !isReset) {
      return;
    }

    if (isReset) {
      setLoading(true);
      dispatch(changeLoaderState(true));
    } else {
      setIsLoadingMore(true);
    }

    try {
      const currentPageNo = isReset ? 0 : pageNo;
      const request = createDepositHistoryRequest(currentPageNo, appliedFilters);
      const newData = await getDepositHistory(request);
      if (newData) {
        setItems((prev) => {
          if (isReset || !prev) {
            return newData;
          }
          return {
            ...prev,
            details: [...prev.details, ...newData.details],
          };
        });
      }

      if (!newData || newData.details.length < 20) {
        setHasMore(false);
      }
      setPageNo((prev) => (isReset ? 1 : prev + 1));
    } finally {
      if (isReset) {
        setLoading(false);
        dispatch(changeLoaderState(false));
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const resetPagination = () => {
    setItems(null);
    setPageNo(0);
    setHasMore(true);

    // Reset UI state for open cards.
    setOpenRowIds([]);
    setValidateTransactionUpdateResponse({});
    setSelectedDetailsItemId(null);
    setSelectedTransactionId(null);
  };

  const handleApply = async (appliedFilters: {
    searchText: string | null;
    fromDate: string | null;
    toDate: string | null;
    statuses: string[];
  }) => {
    setFilters(appliedFilters);
    resetPagination();

    await fetchData(true, appliedFilters);
  };

  const handleRevertConfirmation = async () => {
    if (!selectedDetailsItemId) {
      return;
    }
    let result = await revertUserDepositById(selectedDetailsItemId);
    setIsConfirmationModalOpen(false);
    handleOpenRowIds(selectedDetailsItemId, false);
    setSelectedDetailsItemId(null);
    if (result) {
      toast.success(localization.deposit_success_reverted);
      userPendingStore.getState().fetchPendingData();

      // Refresh data
      setFilters(Default_Filters);
      resetPagination();
      await fetchData(true);
    }
  };

  const getUserLastDepositData = async () => {
    let data = await getUserLastDeposit();
    setIsRevertAllowed(data?.[0]?.isrevertallowed ?? false);
  };

  const handleCardToggle = async (itemId: string, isOpen: boolean) => {
    if (isOpen) {
      let data = await validateUsersDepositTransactionUpdateById(itemId);
      if (data) {
        setValidateTransactionUpdateResponse((prev) => ({
          ...prev,
          [itemId]: data,
        }));
      }
    } else {
      setValidateTransactionUpdateResponse((prev) => {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      });
    }

    handleOpenRowIds(itemId, isOpen);
  };

  const submitDepositTransactionUpdateRequest = async (data: {
    userRemarks: string;
    file: File | null;
  }) => {
    if (!selectedTransactionId || !selectedDetailsItemId) {
      return;
    }

    const depositTransactionUpdateRequest: DepositTransactionUpdateRequest = {};
    const uploadDocumentRequest: UploadDocumentRequest = {};

    if (data.file) {
      uploadDocumentRequest.file = data.file;
      uploadDocumentRequest.fileName = `deposit-${selectedTransactionId}`;

      let imageResult = await uploadTransactionUpdateDocument(
        uploadDocumentRequest,
      );

      if (imageResult) {
        depositTransactionUpdateRequest.transactionUpdateImage = imageResult;
      }
    }

    depositTransactionUpdateRequest.transactionId = selectedTransactionId;
    depositTransactionUpdateRequest.depositId = selectedDetailsItemId;
    depositTransactionUpdateRequest.userRemarks = data.userRemarks;

    let result = await addUsersDepositTransactionUpdateRequest(
      depositTransactionUpdateRequest,
    );

    if (result) {
      toast.success(localization.deposit_transaction_update_sent);
      setIsRequestUpdateModalOpen(false);
      handleOpenRowIds(selectedDetailsItemId, false);
      setSelectedTransactionId(null);
      setSelectedDetailsItemId(null);

      // Refresh data
      setFilters(Default_Filters);
      resetPagination();
      await fetchData(true);
    }
  };

  useEffect(() => {
    const init = async () => {
      await getUserLastDepositData();
      await fetchData(true);
    };

    init();
  }, []);

  const onRevertButtonClick = async (itemId: string) => {
    await getUserLastDepositData();
    setSelectedDetailsItemId(itemId);
    setIsConfirmationModalOpen(true);
  };

  useEffect(() => {
    setIsRevertAllowed(depositRevert);
  }, [depositRevert]);

  useEffect(() => {
    if (!latestDepositUpdate) {
      return;
    }
    if (!data?.details?.length) {
      return;
    }

    setItems((prev) => {
      if (!prev?.details) return prev;

      const index = prev.details.findIndex(
        (item) => item.id === latestDepositUpdate.id,
      );

      if (index === -1) return prev; // Item not on this page

      const updatedDetails = [...prev.details];
      updatedDetails[index] = {
        ...updatedDetails[index],
        status: latestDepositUpdate.status ?? updatedDetails[index].status,
        amount: latestDepositUpdate.amount ?? updatedDetails[index].amount,
        processeddate:
          latestDepositUpdate.processedDate ??
          updatedDetails[index].processeddate,
        bonusamount:
          latestDepositUpdate.bonusAmount ?? updatedDetails[index].bonusamount,
        statusremarks:
          latestDepositUpdate.remarks ?? updatedDetails[index].statusremarks,
      };

      return {
        ...prev,
        details: updatedDetails,
      };
    });
  }, [latestDepositUpdate]);

  return (
    <div>
      <div>
        <TransactionFilter
          filters={filters}
          onFiltersChange={handleFiltersChange}
          statusOptions={[
            localization.pending,
            localization.approved,
            localization.rejected,
          ]}
          onApply={handleApply}
        />
      </div>

      <InfiniteScroll
        onLoadMore={() => fetchData()}
        hasMore={hasMore}
        isLoading={loading || isLoadingMore}
      >
        {!loading && (data?.details?.length ?? 0) === 0 && (
          <div className="text-xl flex justify-center mt-1">
            {localization.no_deposit_history}
          </div>
        )}
        <div className="grid grid-cols-1 md:landscape:grid-cols-2 lg:portrait:grid-cols-1 lg:landscape:grid-cols-2 xl:grid-cols-2 gap-4">
          {(data?.details?.length ?? 0) > 0 &&
            data?.details.map((item) => {
                const validateTransactionUpdate =
                  ValidateTransactionUpdateData[item.id];
                return (
                  <div key={item.id}>
                    <TransactionCard
                      onToggleChange={(isOpen) =>
                        handleCardToggle(item.id, isOpen)
                      }
                    >
                      <div className="grid grid-rows-2 gap-2 text-white">
                        <div>
                          <span className="font-semibold text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-base xl:landscape:text-base text-[#ECECFF] tracking-wide">
                            {item.transactionid}
                            {openRowIds.includes(item.id) && (
                              <img
                                src={"/icons/ic-copy.svg"}
                                alt="Copy"
                                className="size-4 inline ml-2 cursor-pointer"
                                onClick={() =>
                                  common.copyIdToClipboard(
                                    item.transactionid,
                                    localization.transid_success_copied,
                                  )
                                }
                              />
                            )}
                          </span>

                          {isRevertAllowed &&
                            item.status === TransactionStatusTypes.PENDING && (
                              <Button
                                onClick={() => onRevertButtonClick(item.id)}
                                className="flex float-right font-medium ml-2 p-1 text-xs md:text-sm rounded-sm bg-[#341A00] text-[#F69C42]"
                              >
                                <img
                                  src="/icons/ic-revert.svg"
                                  alt="Revert"
                                  className="size-4 inline mr-1"
                                />
                                {localization.revert}
                              </Button>
                            )}

                          <span
                            className={`float-right font-medium p-1 text-xs md:portrait:text-sm md:landscape:text-xs lg:landscape:text-xs lg:portrait:text-sm xl:landscape:text-sm rounded-sm ${DEPOSIT_STATUS_STYLE[item.status]
                              }`}
                          >
                            {localization.getString(item.status.toLowerCase())}
                          </span>
                        </div>

                        <div className="font-medium text-sm text-[#A9A9A9]">
                          <span>
                            {formatUtcToLocalDateTime(item.transactiondate)}
                          </span>
                          <span className="float-right text-[#E6E6E6]">
                            {`${item.amount.toFixed(2)}\u20B9`}
                          </span>
                        </div>

                        {openRowIds.includes(item.id) && (
                          <div className="grid grid-rows-2 gap-2 border-t border-[#353535] mt-2 pt-2 font-medium text-sm text-[#A9A9A9]">
                            <div>
                              <span>{localization.deposit_type}</span>
                              <img
                                src={
                                  item?.deposittypelogourl
                                    ? `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${item.deposittypelogourl}`
                                    : "/images/icon-upload.png"
                                }
                                alt="Deposit Type"
                                className="size-6 float-right"
                              />
                            </div>

                            <div>
                              <span>{localization.processed_date}</span>
                              <span className="float-right text-white">
                                {item.status === TransactionStatusTypes.PENDING
                                  ? "-"
                                  : formatUtcToLocalDateTime(
                                    item.processeddate,
                                  )}
                              </span>
                            </div>

                            <div>
                              <span>{localization.reference_number}</span>
                              <span className="float-right text-white">
                                {item.referencenumber}
                              </span>
                            </div>

                            {item.status ===
                              TransactionStatusTypes.REJECTED && (
                                <div>
                                  <span>{localization.remark}</span>
                                  <span className="float-right text-white">
                                    {item.statusremarks}
                                  </span>
                                </div>
                              )}

                            {item.bonusname && (
                              <div className="grid grid-rows-2 gap-2">
                                <div>
                                  <span>{localization.bonus_name}</span>
                                  <span className="float-right text-white">
                                    {item.bonusname}
                                  </span>
                                </div>

                                <div>
                                  <span>{localization.bonus_amount}</span>
                                  <span className="float-right text-white">
                                    {item.bonusamount}
                                  </span>
                                </div>
                              </div>
                            )}

                            {validateTransactionUpdate &&
                              validateTransactionUpdate.isUpdateVisible && (
                                <div>
                                  {!validateTransactionUpdate.isDisabled && (
                                    <div>
                                      <Button
                                        onClick={() => {
                                          setSelectedTransactionId(
                                            item.transactionid,
                                          );
                                          setSelectedDetailsItemId(item.id);
                                          setIsRequestUpdateModalOpen(true);
                                        }}
                                        className="w-full font-medium text-sm md:text-base border-2 border-[#9E59B7] rounded-[4px] bg-black text-white my-2 py-2 tracking-wide"
                                      >
                                        <img
                                          src="/icons/ic-repeat.svg"
                                          alt="Repeat"
                                          className="size-4 inline mr-2"
                                        />
                                        {localization.request_update_btn}
                                      </Button>
                                    </div>
                                  )}

                                  {validateTransactionUpdate.isDisabled && (
                                    <div className="text-center my-2 text-[#DDCD32]">
                                      <p className="mx-4">
                                        {` ${localization.update_transaction_text1} ${localization.update_transaction_text2}`}
                                      </p>
                                      <p>
                                        {localization.update_transaction_text3}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </TransactionCard>
                  </div>
                );
            })}
        </div>
      </InfiniteScroll>

      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onConfirm={() => handleRevertConfirmation()}
          onCancel={() => {
            setIsConfirmationModalOpen(false);
            setSelectedDetailsItemId(null);
          }}
          title={localization.deposit_revert_confirmation_lable}
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText={localization.yes.toUpperCase()}
          cancelText={localization.no.toUpperCase()}
        />
      )}

      {isRequestUpdateModalOpen && (
        <UploadDocumentModal
          isOpen={isRequestUpdateModalOpen}
          onSubmit={(data) => submitDepositTransactionUpdateRequest(data)}
          onCancel={() => {
            setIsRequestUpdateModalOpen(false);
            setSelectedTransactionId(null);
            setSelectedDetailsItemId(null);
          }}
          title={localization.upload_document_text1}
          warningText={localization.upload_document_text2}
          blurColor="bg-[#DBEEFF]"
          submitText={localization.upload_without_attachment_btn}
        />
      )}
    </div>
  );
}
